import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate, useLocation } from 'react-router-dom';
import { MainLayout } from '@/presentation/layouts/MainLayout';
import { LoginPage } from '@/presentation/pages/LoginPage';
import { DashboardPage } from '@/presentation/pages/DashboardPage';
import { TenantsPage } from '@/presentation/pages/TenantsPage';
import { MigracionFrontmyPage } from '@/presentation/pages/MigracionFrontmyPage';
import { PlansPage } from '@/presentation/pages/PlansPage';
import { AdicionalesPage } from '@/presentation/pages/AdicionalesPage';
import { CoofitConexionPage } from '@/presentation/pages/CoofitConexionPage';
import { CoofitMarketingPage } from '@/presentation/pages/CoofitMarketingPage';
import { ComercialLayout } from '@/presentation/pages/ComercialLayout';
import { FinanceSummaryPage } from '@/presentation/pages/FinanceSummaryPage';
import { ExpensesPage } from '@/presentation/pages/ExpensesPage';
import { PaymentsLayout } from '@/presentation/pages/PaymentsLayout';
import { ReceivedPaymentsPage } from '@/presentation/pages/ReceivedPaymentsPage';
import { PeriodOpsPage } from '@/presentation/pages/PeriodOpsPage';
import { PeriodSettingsPage } from '@/presentation/pages/PeriodSettingsPage';
import { CollectionsPage } from '@/presentation/pages/CollectionsPage';
import { EcosystemUsagePage } from '@/presentation/pages/EcosystemUsagePage';
import { ClinicInvoicesPage } from '@/presentation/pages/ClinicInvoicesPage';
import { PaymentNoticesPage } from '@/presentation/pages/PaymentNoticesPage';
import { ReminderAnalyticsPage } from '@/presentation/pages/ReminderAnalyticsPage';
import { SupportPage } from '@/presentation/pages/SupportPage';
import { EmailsPage } from '@/presentation/pages/EmailsPage';
import { DocumentationPage } from '@/presentation/pages/DocumentationPage';
import { DocumentationArticlePage } from '@/presentation/pages/DocumentationArticlePage';
import { SolicitudesPage } from '@/presentation/pages/SolicitudesPage';
import { SettingsPage } from '@/presentation/pages/SettingsPage';
import { EmailTemplatesPage } from '@/presentation/pages/EmailTemplatesPage';
import { ExpenseCategoriesPage } from '@/presentation/pages/ExpenseCategoriesPage';
import { HolidaysPage } from '@/presentation/pages/HolidaysPage';
import { CostProjectionPage } from '@/presentation/pages/CostProjectionPage';
import { LogsPage } from '@/presentation/pages/LogsPage';
import { AuditoriasPage } from '@/presentation/pages/AuditoriasPage';
import { PanelUsersPage } from '@/presentation/pages/PanelUsersPage';
import { PaymentSuccessPage } from '@/presentation/pages/PaymentSuccessPage';
import { PaymentErrorPage } from '@/presentation/pages/PaymentErrorPage';
import { PaymentPendingPage } from '@/presentation/pages/PaymentPendingPage';
import { ProtectedRoute } from '@/presentation/components/common/ProtectedRoute';

/** Redirige una ruta vieja conservando ?query y #hash (ej. /collections?q=...). */
const RedirectTo: React.FC<{ to: string }> = ({ to }) => {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
};

const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/pago/ok',
    element: <PaymentSuccessPage />,
  },
  {
    path: '/pago/error',
    element: <PaymentErrorPage />,
  },
  {
    path: '/pago/pendiente',
    element: <PaymentPendingPage />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: <DashboardPage />,
      },
      {
        path: 'asistente',
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'tenants',
        element: <TenantsPage />,
      },
      {
        path: 'migracion-frontmy',
        element: <MigracionFrontmyPage />,
      },
      {
        path: 'plans',
        element: <PlansPage />,
      },
      {
        path: 'comercial',
        element: <ComercialLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="conexion" replace />,
          },
          {
            path: 'conexion',
            element: <CoofitConexionPage />,
          },
          {
            path: 'marketing',
            element: <CoofitMarketingPage />,
          },
        ],
      },
      {
        path: 'coofit-conexion',
        element: <Navigate to="/comercial/conexion" replace />,
      },
      {
        path: 'coofit-marketing',
        element: <Navigate to="/comercial/marketing" replace />,
      },
      {
        path: 'adicionales',
        element: <AdicionalesPage />,
      },
      {
        path: 'finanzas',
        children: [
          {
            index: true,
            element: <Navigate to="resumen" replace />,
          },
          {
            path: 'resumen',
            element: <FinanceSummaryPage />,
          },
          // Pestañas del Resumen anterior (período / histórico / reportes): hoy es una sola pantalla.
          {
            path: 'resumen/*',
            element: <RedirectTo to="/finanzas/resumen" />,
          },
          {
            path: 'facturas',
            element: <ClinicInvoicesPage />,
          },
          {
            path: 'pagos',
            element: <PaymentsLayout />,
            children: [
              {
                index: true,
                element: <Navigate to="recibidos" replace />,
              },
              {
                path: 'recibidos',
                element: <ReceivedPaymentsPage />,
              },
              {
                path: 'informados',
                element: <PaymentNoticesPage />,
              },
            ],
          },
          {
            path: 'deudores',
            element: <CollectionsPage />,
          },
          {
            path: 'gastos',
            element: <ExpensesPage />,
          },
        ],
      },
      // Rutas viejas de Finanzas (links guardados, mails y WhatsApp del panel).
      {
        path: 'payments',
        element: <RedirectTo to="/finanzas/resumen" />,
      },
      {
        path: 'panel-facturacion',
        element: <RedirectTo to="/finanzas/resumen" />,
      },
      {
        path: 'panel-facturacion-historico',
        element: <RedirectTo to="/finanzas/resumen" />,
      },
      {
        path: 'reports',
        element: <RedirectTo to="/finanzas/resumen" />,
      },
      {
        path: 'collections',
        element: <RedirectTo to="/finanzas/deudores" />,
      },
      {
        path: 'facturas-clinicas',
        element: <RedirectTo to="/finanzas/facturas" />,
      },
      {
        path: 'pagos-informados',
        element: <RedirectTo to="/finanzas/pagos/informados" />,
      },
      {
        path: 'ecosystem-usage',
        element: <EcosystemUsagePage />,
      },
      {
        path: 'reminder-analytics',
        element: <ReminderAnalyticsPage />,
      },
      {
        path: 'period-ops',
        element: (
          <ProtectedRoute require="administer">
            <PeriodOpsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'balance',
        element: <RedirectTo to="/finanzas/resumen" />,
      },
      {
        path: 'support',
        element: <SupportPage />,
      },
      {
        path: 'emails',
        element: <EmailsPage />,
      },
      {
        path: 'documentation',
        element: <DocumentationPage />,
      },
      {
        path: 'documentation/articles/:id',
        element: <DocumentationArticlePage />,
      },
      {
        path: 'solicitudes',
        element: <SolicitudesPage />,
      },
      {
        path: 'configuracion',
        element: <Navigate to="/configuracion-whatsapp" replace />,
      },
      {
        path: 'configuracion-app',
        element: <SettingsPage section="app" />,
      },
      {
        path: 'configuracion-periodo',
        element: (
          <ProtectedRoute require="administer">
            <PeriodSettingsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'configuracion-whatsapp',
        element: <SettingsPage section="whatsapp" />,
      },
      {
        path: 'configuracion-email',
        element: <EmailTemplatesPage />,
      },
      {
        path: 'feriados',
        element: (
          <ProtectedRoute require="administer">
            <HolidaysPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'categorias-gastos',
        element: <ExpenseCategoriesPage />,
      },
      {
        path: 'configuracion-usuarios',
        element: (
          <ProtectedRoute require="manageUsers">
            <PanelUsersPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'proyeccion-costos',
        element: <CostProjectionPage />,
      },
      {
        path: 'auditorias',
        element: (
          <ProtectedRoute require="administer">
            <AuditoriasPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'logs',
        element: (
          <ProtectedRoute require="administer">
            <LogsPage />
          </ProtectedRoute>
        ),
      },
    ],
  },
]);

export const AppRouter: React.FC = () => {
  return <RouterProvider router={router} />;
};
